import { useState, useEffect } from 'react';
import { CurrencyInput } from '../../utils/currencyFormat';
import {
  getExpenses,
  getExpenseStats,
  createExpense,
  updateExpense,
  deleteExpense,
  type Expense,
  type ExpenseStats,
  type CreateExpenseDto,
  type UpdateExpenseDto,
  type ExpenseFilters,
} from '../../api/expenses.api';

const CATEGORIAS = [
  { value: 'NOMINA', label: 'Nómina', icon: 'ri-team-line', color: 'blue' },
  { value: 'SERVICIOS_PUBLICOS', label: 'Servicios Públicos', icon: 'ri-lightbulb-line', color: 'yellow' },
  { value: 'MANTENIMIENTO', label: 'Mantenimiento', icon: 'ri-tools-line', color: 'orange' },
  { value: 'SEGURIDAD', label: 'Seguridad', icon: 'ri-shield-line', color: 'red' },
  { value: 'IMPUESTOS', label: 'Impuestos', icon: 'ri-file-list-3-line', color: 'purple' },
  { value: 'PAPELERIA', label: 'Papelería', icon: 'ri-file-paper-line', color: 'gray' },
  { value: 'TECNOLOGIA', label: 'Tecnología', icon: 'ri-computer-line', color: 'indigo' },
  { value: 'LIMPIEZA', label: 'Limpieza', icon: 'ri-brush-line', color: 'green' },
  { value: 'MARKETING', label: 'Marketing', icon: 'ri-megaphone-line', color: 'pink' },
  { value: 'OTRO', label: 'Otro', icon: 'ri-more-line', color: 'slate' },
];

const ESTADOS = [
  { value: 'PENDIENTE', label: 'Pendiente', color: 'yellow' },
  { value: 'PAGADO', label: 'Pagado', color: 'green' },
  { value: 'ANULADO', label: 'Anulado', color: 'red' },
];

export default function GastosPage() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [stats, setStats] = useState<ExpenseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [filters, setFilters] = useState<ExpenseFilters>({});
  
  // Form state
  const [formData, setFormData] = useState<CreateExpenseDto>({
    concepto: '',
    categoria: 'OTRO',
    monto: 0,
    proveedor: '',
    numeroFactura: '',
    observaciones: '',
    shiftId: undefined,
    metodoPago: 'EFECTIVO',
    status: 'PAGADO',
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [expensesData, statsData] = await Promise.all([
        getExpenses(filters),
        getExpenseStats().catch(() => null), // Solo ADMIN puede ver stats
      ]);
      setExpenses(expensesData);
      setStats(statsData);
    } catch (error: any) {
      console.error('Error loading data:', error);
      if (error?.response?.status === 403) setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  };

  // Cargar turnos para el select (solo admin)
  const [shifts, setShifts] = useState<any[]>([]);
  const userRole = localStorage.getItem('userRole');

  useEffect(() => {
    if (userRole === 'ADMIN' || userRole === 'SUPERADMIN') {
        import('../../api/shifts.api').then(({ getShiftsHistory }) => {
            getShiftsHistory().then(data => {
                // Mostrar solo turnos abiertos o recientes (los ultimos 20)
                setShifts(data.slice(0, 20));
            });
        });
    }
  }, [userRole]);

  const handleCreate = async () => {
    try {
      await createExpense(formData);
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating expense:', error);
      alert('Error al crear el gasto');
    }
  };

  const handleUpdate = async () => {
    if (!editingExpense) return;
    try {
      await updateExpense(editingExpense.id, formData as UpdateExpenseDto);
      setShowModal(false);
      setEditingExpense(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error updating expense:', error);
      alert('Error al actualizar el gasto');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este gasto?')) return;
    try {
      await deleteExpense(id);
      loadData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Error al eliminar el gasto');
    }
  };

  const openEditModal = (expense: Expense) => {
    setEditingExpense(expense);
    setFormData({
      concepto: expense.concepto,
      categoria: expense.categoria,
      monto: expense.monto,
      proveedor: expense.proveedor || '',
      numeroFactura: expense.numeroFactura || '',
      observaciones: expense.observaciones || '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      concepto: '',
      categoria: 'OTRO',
      monto: 0,
      proveedor: '',
      numeroFactura: '',
      observaciones: '',
      shiftId: undefined,
      metodoPago: 'EFECTIVO',
      status: 'PAGADO',
    });
    setEditingExpense(null);
  };

  const getCategoriaInfo = (categoria: string) => {
    return CATEGORIAS.find((c) => c.value === categoria) || CATEGORIAS[CATEGORIAS.length - 1];
  };

  const getEstadoInfo = (status: string) => {
    return ESTADOS.find((e) => e.value === status) || ESTADOS[0];
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <i className="ri-loader-4-line animate-spin text-4xl text-blue-600"></i>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="max-w-xl mx-auto mt-16 p-8 bg-white rounded-2xl border border-slate-200 text-center">
        <i className="ri-lock-line text-4xl text-slate-400"></i>
        <h2 className="text-xl font-bold text-slate-800 mt-3">Sin acceso a Gastos</h2>
        <p className="text-slate-500 mt-2">
          Tu empresa no tiene habilitado el registro de gastos para operadores. Pide a un administrador que lo active en Configuración.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gastos</h1>
          <p className="text-slate-500 text-sm">Gestiona los gastos operativos del parqueadero</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition flex items-center gap-2"
        >
          <i className="ri-add-line"></i>
          Nuevo Gasto
        </button>
      </div>

      {/* Stats (solo visible para ADMIN) */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <i className="ri-money-dollar-circle-line text-3xl mb-2 opacity-80"></i>
            <p className="text-sm opacity-80 mb-1">Total Gastado</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.totalGastado)}</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl p-6 text-white">
            <i className="ri-time-line text-3xl mb-2 opacity-80"></i>
            <p className="text-sm opacity-80 mb-1">Gastos Pendientes</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.totalPendiente)}</p>
            <p className="text-xs opacity-70">{stats.cantidadPendientes} gastos</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white">
            <i className="ri-check-line text-3xl mb-2 opacity-80"></i>
            <p className="text-sm opacity-80 mb-1">Gastos Pagados</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.totalPagado)}</p>
            <p className="text-xs opacity-70">{stats.cantidadPagados} gastos</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
            <i className="ri-calendar-line text-3xl mb-2 opacity-80"></i>
            <p className="text-sm opacity-80 mb-1">Mes Actual</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.mesActual.total)}</p>
            <p className="text-xs opacity-70">{stats.mesActual.cantidad} gastos</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Categoría</label>
            <select
              value={filters.categoria || ''}
              onChange={(e) => setFilters({ ...filters, categoria: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="">Todas</option>
              {CATEGORIAS.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Estado</label>
            <select
              value={filters.status || ''}
              onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="">Todos</option>
              {ESTADOS.map((estado) => (
                <option key={estado.value} value={estado.value}>
                  {estado.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Fecha Inicio</label>
            <input
              type="date"
              value={filters.fechaInicio || ''}
              onChange={(e) => setFilters({ ...filters, fechaInicio: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Fecha Fin</label>
            <input
              type="date"
              value={filters.fechaFin || ''}
              onChange={(e) => setFilters({ ...filters, fechaFin: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      {/* Expenses Table - Desktop View */}
      <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Fecha</th>
                <th className="text-left px-4 py-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Concepto</th>
                <th className="text-left px-4 py-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Categoría</th>
                <th className="text-left px-4 py-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Proveedor</th>
                <th className="text-right px-4 py-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Monto</th>
                <th className="text-center px-4 py-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Estado</th>
                <th className="text-center px-4 py-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-slate-400">
                    <i className="ri-inbox-line text-5xl mb-3 block"></i>
                    <p className="text-lg font-medium">No hay gastos registrados</p>
                    <p className="text-sm mt-1">Los gastos que registres aparecerán aquí</p>
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => {
                  const catInfo = getCategoriaInfo(expense.categoria);
                  const statusInfo = getEstadoInfo(expense.status);
                  return (
                    <tr key={expense.id} className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-slate-600">
                          {formatDate(expense.fecha)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="max-w-xs">
                          <p className="font-semibold text-slate-800 truncate">{expense.concepto}</p>
                          {expense.observaciones && (
                            <p className="text-xs text-slate-400 mt-1 truncate">{expense.observaciones}</p>
                          )}
                          {expense.numeroFactura && (
                            <p className="text-xs text-slate-500 mt-1">Factura: {expense.numeroFactura}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-${catInfo.color}-50 text-${catInfo.color}-700 text-xs font-medium shadow-sm`}>
                          <i className={`${catInfo.icon} text-sm`}></i>
                          <span className="hidden xl:inline">{catInfo.label}</span>
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600">{expense.proveedor || '-'}</td>
                      <td className="px-4 py-4 text-right">
                        <span className="font-bold text-slate-900 text-base">{formatCurrency(expense.monto)}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-block px-3 py-1.5 rounded-lg bg-${statusInfo.color}-50 text-${statusInfo.color}-700 text-xs font-semibold shadow-sm`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openEditModal(expense)}
                            className="text-blue-600 hover:text-white hover:bg-blue-600 p-2 rounded-lg transition-all duration-150"
                            title="Editar"
                          >
                            <i className="ri-edit-line text-lg"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(expense.id)}
                            className="text-red-600 hover:text-white hover:bg-red-600 p-2 rounded-lg transition-all duration-150"
                            title="Eliminar"
                          >
                            <i className="ri-delete-bin-line text-lg"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expenses Cards - Mobile/Tablet View */}
      <div className="lg:hidden space-y-4">
        {expenses.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center text-slate-400">
            <i className="ri-inbox-line text-5xl mb-3 block"></i>
            <p className="text-lg font-medium">No hay gastos registrados</p>
            <p className="text-sm mt-1">Los gastos que registres aparecerán aquí</p>
          </div>
        ) : (
          expenses.map((expense) => {
            const catInfo = getCategoriaInfo(expense.categoria);
            const statusInfo = getEstadoInfo(expense.status);
            return (
              <div
                key={expense.id}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-4 py-3 border-b border-slate-200">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 text-base truncate">{expense.concepto}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{formatDate(expense.fecha)}</p>
                    </div>
                    <span className={`inline-block px-3 py-1.5 rounded-lg bg-${statusInfo.color}-50 text-${statusInfo.color}-700 text-xs font-semibold shadow-sm whitespace-nowrap`}>
                      {statusInfo.label}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-3">
                  {/* Monto destacado */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium mb-1">Monto</p>
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(expense.monto)}</p>
                  </div>

                  {/* Detalles en grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1.5 font-medium">Categoría</p>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-${catInfo.color}-100 text-${catInfo.color}-700 text-xs font-medium`}>
                        <i className={`${catInfo.icon}`}></i>
                        <span>{catInfo.label}</span>
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1.5 font-medium">Proveedor</p>
                      <p className="text-sm font-semibold text-slate-800 truncate">{expense.proveedor || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Info adicional */}
                  {expense.numeroFactura && (
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1 font-medium">N° Factura</p>
                      <p className="text-sm font-semibold text-slate-800">{expense.numeroFactura}</p>
                    </div>
                  )}

                  {expense.observaciones && (
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                      <p className="text-xs text-amber-600 mb-1 font-medium flex items-center gap-1">
                        <i className="ri-information-line"></i>
                        Observaciones
                      </p>
                      <p className="text-sm text-slate-700 line-clamp-2">{expense.observaciones}</p>
                    </div>
                  )}
                </div>

                {/* Card Footer - Acciones */}
                <div className="bg-slate-50 px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                  <button
                    onClick={() => openEditModal(expense)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors duration-150 text-sm"
                  >
                    <i className="ri-edit-line"></i>
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(expense.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors duration-150 text-sm"
                  >
                    <i className="ri-delete-bin-line"></i>
                    Eliminar
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">
                {editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Concepto <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.concepto}
                  onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg"
                  placeholder="Ej: Pago de luz"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Categoría <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg"
                  >
                    {CATEGORIAS.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Monto <span className="text-red-500">*</span>
                  </label>
                  <CurrencyInput
                    value={formData.monto}
                    onChange={(value) => setFormData({ ...formData, monto: value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Proveedor</label>
                  <input
                    type="text"
                    value={formData.proveedor}
                    onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg"
                    placeholder="Ej: EPM"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">N° Factura</label>
                  <input
                    type="text"
                    value={formData.numeroFactura}
                    onChange={(e) => setFormData({ ...formData, numeroFactura: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg"
                    placeholder="Ej: FAC-001234"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                   <label className="block text-sm font-bold text-slate-700 mb-2">Método de Pago</label>
                   <select
                        value={formData.metodoPago || ''}
                        onChange={(e) => setFormData({ ...formData, metodoPago: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm"
                   >
                        <option value="">-- Seleccionar --</option>
                        <option value="EFECTIVO">Efectivo</option>
                        <option value="TARJETA">Tarjeta</option>
                        <option value="TRANSFERENCIA">Transferencia</option>
                        <option value="QR">QR</option>
                   </select>
                </div>
                <div>
                   <label className="block text-sm font-bold text-slate-700 mb-2">Estado</label>
                   <select
                        value={formData.status || 'PENDIENTE'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm"
                   >
                        <option value="PENDIENTE">Pendiente</option>
                        <option value="PAGADO">Pagado</option>
                   </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Observaciones</label>
                <textarea
                  value={formData.observaciones}
                  onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg"
                  rows={3}
                  placeholder="Notas adicionales..."
                ></textarea>
              </div>
              {(userRole === 'ADMIN' || userRole === 'SUPERADMIN') && (
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Vincular a Caja / Turno (Opcional)</label>
                    <select
                        value={formData.shiftId || ''}
                        onChange={(e) => setFormData({ ...formData, shiftId: e.target.value || undefined })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm"
                    >
                        <option value="">-- Automático (Turno del Operador) --</option>
                        {shifts.map((shift: any) => (
                            <option key={shift.id} value={shift.id}>
                                {shift.operador?.nombre} - {new Date(shift.apertura).toLocaleString()} ({shift.status})
                            </option>
                        ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                        Si se deja vacío, el sistema intentará vincularlo al turno abierto del usuario que crea el gasto.
                    </p>
                  </div>
              )}
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="px-6 py-3 border border-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={editingExpense ? handleUpdate : handleCreate}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700"
              >
                {editingExpense ? 'Actualizar' : 'Crear Gasto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
